import {
  FeedbackItem,
  FeedbackItemRequest,
  KraArea,
  KraObjective,
  MAX_ACHIEVEMENT_LENGTH,
  MAX_GOAL_ROWS,
  REQUIRED_TOTAL_WEIGHTAGE,
} from "services/Feedback/feedbackInterface";

/**
 * Goal-sheet form helpers — pure functions, shared by the self-feedback editor
 * and the manager-feedback form (which may also carry goal rows).
 *
 * NOTE: these mirror the server's validation rules so the user is stopped
 * before the round trip. The server remains the authority; this only avoids
 * avoidable 400s and keeps the error next to the field that caused it.
 */

// ── Row model ────────────────────────────────────────────────────────────────

/**
 * A goal row while it is being edited.
 *
 * NOTE: rows are REPLACED on every save and the server hands back new row ids,
 * so React keys must not use `id`. `uid` is a stable client-only key that
 * survives a save; it is never sent.
 */
export interface GoalRowDraft {
  uid: string;
  areaId: number | null;
  objectiveId: number | null;
  weightage: string;
  /** CHANGE: the one "KPI / achievement" box; the separate `kpi` draft field is gone. */
  achievement: string;
  rating: number | null;
  /**
   * Names captured from a saved row, used when the area or objective has since
   * been deactivated and so is missing from the dropdowns. Without these the
   * row would render blank and the user would be forced to re-pick it.
   */
  savedAreaName?: string;
  savedObjectiveName?: string;
}

let uidCounter = 0;
const nextUid = (): string => {
  uidCounter += 1;
  return `goal-${uidCounter}`;
};

export const createEmptyGoalRow = (): GoalRowDraft => ({
  uid: nextUid(),
  areaId: null,
  objectiveId: null,
  weightage: "",
  achievement: "",
  rating: null,
});

/** Maps saved rows into editable drafts, keeping the saved display names. */
export const toGoalRowDrafts = (items?: FeedbackItem[] | null): GoalRowDraft[] => {
  if (!items || items.length === 0) return [createEmptyGoalRow()];

  return items.map((item) => ({
    uid: nextUid(),
    areaId: item.areaId,
    objectiveId: item.objectiveId,
    weightage: item.weightage === null ? "" : String(item.weightage),
    achievement: item.achievement ?? "",
    rating: item.rating,
    savedAreaName: item.areaName,
    savedObjectiveName: item.objectiveName,
  }));
};

/**
 * Seeds an APPROVING reviewer's rows from the employee's submitted sheet.
 *
 * CHANGE: new. Approving feedback may now carry goal rows, and the contract
 * says to prefill the employee's rows so the reviewer scores each goal.
 *
 * NOTE: area, objective and weightage carry over — the reviewer is scoring the
 * employee's goals, not inventing new ones — but `achievement` and `rating` are
 * deliberately BLANK. On an approving record those two are the reviewer's own
 * comment and score for that goal; copying the employee's across would put
 * words in the reviewer's mouth and let them approve without reading.
 *
 * Returns [] for a sheet with no rows, so the row editor stays empty rather
 * than showing one blank row the reviewer never asked for.
 */
export const toApproverRowDrafts = (items?: FeedbackItem[] | null): GoalRowDraft[] => {
  if (!items || items.length === 0) return [];

  return items.map((item) => ({
    uid: nextUid(),
    areaId: item.areaId,
    objectiveId: item.objectiveId,
    weightage: item.weightage === null ? "" : String(item.weightage),
    achievement: "",
    rating: null,
    savedAreaName: item.areaName,
    savedObjectiveName: item.objectiveName,
  }));
};

/**
 * Converts drafts into the API row shape.
 *
 * Rows with no area or objective are dropped: a blank trailing row is a normal
 * editing state, not something to send.
 */
export const toItemRequests = (rows: GoalRowDraft[]): FeedbackItemRequest[] =>
  rows
    .filter(isSendableRow)
    .map((row) => ({
      areaId: row.areaId as number,
      objectiveId: row.objectiveId as number,
      weightage: row.weightage.trim() === "" ? null : Number(row.weightage),
      achievement: row.achievement.trim() === "" ? null : row.achievement.trim(),
      rating: row.rating,
    }));

/**
 * A row the user has BEGUN filling in. Counts toward the 5-row cap and gets
 * validated, even though it may not be complete enough to send yet.
 */
export const isStartedRow = (row: GoalRowDraft): boolean =>
  row.areaId !== null || row.objectiveId !== null;

/** A row complete enough to SEND. `toItemRequests` drops everything else. */
export const isSendableRow = (row: GoalRowDraft): boolean =>
  row.areaId !== null && row.objectiveId !== null;

/**
 * Running total for the weightage meter.
 *
 * CHANGE (bug fix): only SENDABLE rows count. Previously every row counted, so
 * a row carrying a weightage but no area or objective was added to the meter
 * AND to the `total === 100` submit gate, while `toItemRequests` dropped it
 * from the request. The meter read 100, the server received 70 and refused the
 * submit — with no field to point the user at.
 */
export const calculateTotalWeightage = (rows: GoalRowDraft[]): number =>
  rows.filter(isSendableRow).reduce((sum, row) => {
    const value = Number(row.weightage);
    return sum + (Number.isFinite(value) ? value : 0);
  }, 0);

/** Only rows the user has actually started count toward the 5-row cap. */
export const countFilledRows = (rows: GoalRowDraft[]): number => rows.filter(isStartedRow).length;

/**
 * The LOOSE rule: is there a free slot at all.
 *
 * NOTE: used by the manager feedback form, where rows are optional extras —
 * weightage is all-or-nothing and the per-goal comment is never required, so
 * the stricter self-sheet rule below would block legitimate input there.
 */
export const canAddGoalRow = (rows: GoalRowDraft[]): boolean =>
  countFilledRows(rows) < MAX_GOAL_ROWS && rows.length < MAX_GOAL_ROWS;

/** A row carrying everything a submit needs: area, objective, a valid weightage and text. */
export const isCompleteRow = (row: GoalRowDraft): boolean => {
  if (!isSendableRow(row)) return false;

  const weightage = Number(row.weightage.trim());
  if (!Number.isInteger(weightage) || weightage < 1 || weightage > 100) return false;

  return row.achievement.trim() !== "";
};

/**
 * Why another goal row can't be added to a SELF sheet right now — or null when
 * it can.
 *
 * CHANGE: new. `canAddGoalRow` only ever counted rows, so "Add goal" stayed
 * live in two states where a new row is meaningless:
 *   - the weightages already reach (or exceed) 100%, and every row must fit
 *     inside that budget, so there is nothing left to give a sixth goal;
 *   - the row above is still half-filled, which let someone stack five blank
 *     rows and meet all five validation errors at once on submit.
 *
 * NOTE: returns the REASON, not a boolean. A disabled button with no
 * explanation reads as a bug; the screen prints this string beside it.
 */
export const getAddGoalBlockReason = (rows: GoalRowDraft[]): string | null => {
  if (rows.length >= MAX_GOAL_ROWS) {
    return `All ${MAX_GOAL_ROWS} goals are in use.`;
  }

  // Named by position, so the message points at the row to go and fix.
  const unfinished = rows.findIndex((row) => !isCompleteRow(row));
  if (unfinished !== -1) {
    return `Finish goal ${unfinished + 1} before adding another.`;
  }

  const total = calculateTotalWeightage(rows);
  if (total > REQUIRED_TOTAL_WEIGHTAGE) {
    return `Weightage is ${total - REQUIRED_TOTAL_WEIGHTAGE}% over 100% — fix that first.`;
  }
  if (total === REQUIRED_TOTAL_WEIGHTAGE) {
    return "Weightage already totals 100% — lower a goal to make room.";
  }

  return null;
};

// ── Dropdown options ─────────────────────────────────────────────────────────

/**
 * Objectives selectable for a row.
 *
 * Two rules the server enforces and the dropdown should pre-empt:
 *  - the objective must belong to the chosen area
 *  - each objective may appear once per sheet
 *
 * The row's own current objective is always kept, so re-opening a saved sheet
 * never shows an empty select.
 */
export interface ObjectiveOptionsParams {
  areas: KraArea[];
  areaId: number | null;
  rows: GoalRowDraft[];
  currentRowUid: string;
}

export const getObjectiveOptions = ({
  areas,
  areaId,
  rows,
  currentRowUid,
}: ObjectiveOptionsParams): KraObjective[] => {
  if (areaId === null) return [];

  const area = areas.find((candidate) => candidate.id === areaId);
  if (!area) return [];

  const takenElsewhere = new Set(
    rows
      .filter((row) => row.uid !== currentRowUid && row.objectiveId !== null)
      .map((row) => row.objectiveId as number),
  );

  return area.objectives.filter((objective) => !takenElsewhere.has(objective.id));
};

// ── Validation ───────────────────────────────────────────────────────────────

/** Per-row field errors, keyed by the row's `uid`. */
export type GoalRowErrors = Record<string, Partial<Record<keyof GoalRowDraft, string>>>;

export interface SelfFeedbackValidationResult {
  rowErrors: GoalRowErrors;
  /** Errors about the sheet as a whole (row count, weightage total). */
  formError: string;
  isValid: boolean;
}

export interface ValidateSelfFeedbackParams {
  rows: GoalRowDraft[];
  /**
   * Draft saves skip weightage, KPI and the 100% total — the server does the
   * same, which is the whole point of a draft.
   */
  submit: boolean;
}

export const validateSelfFeedback = ({
  rows,
  submit,
}: ValidateSelfFeedbackParams): SelfFeedbackValidationResult => {
  const rowErrors: GoalRowErrors = {};
  let formError = "";

  const filled = rows.filter(isStartedRow);

  if (filled.length > MAX_GOAL_ROWS) {
    formError = `At most ${MAX_GOAL_ROWS} goal rows.`;
  }

  if (submit && filled.length === 0) {
    formError = "Add at least one goal row to submit.";
  }

  const seenObjectives = new Set<number>();

  filled.forEach((row) => {
    const errors: Partial<Record<keyof GoalRowDraft, string>> = {};

    if (row.areaId === null) errors.areaId = "Pick a KRA area.";
    if (row.objectiveId === null) {
      errors.objectiveId = "Pick an objective.";
    } else if (seenObjectives.has(row.objectiveId)) {
      // The server rejects a repeat; catching it here names the offending row.
      errors.objectiveId = "This objective is already used on another row.";
    } else {
      seenObjectives.add(row.objectiveId);
    }

    const weightage = row.weightage.trim();
    if (weightage !== "") {
      const value = Number(weightage);
      if (!Number.isInteger(value) || value < 1 || value > 100) {
        errors.weightage = "1 - 100.";
      }
    } else if (submit) {
      errors.weightage = "Required to submit.";
    }

    // CHANGE: the submit requirement moved from `kpi` to `achievement`. The
    // contract has ONE "KPI / achievement" box per row and rejects a submit
    // without it: "Each goal row needs KPI / achievement to submit".
    if (submit && row.achievement.trim() === "") {
      errors.achievement = "Required to submit.";
    } else if (row.achievement.length > MAX_ACHIEVEMENT_LENGTH) {
      errors.achievement = `Max ${MAX_ACHIEVEMENT_LENGTH} characters.`;
    }

    if (Object.keys(errors).length > 0) rowErrors[row.uid] = errors;
  });

  if (submit && !formError) {
    const total = calculateTotalWeightage(rows);
    if (total !== REQUIRED_TOTAL_WEIGHTAGE) {
      formError = `Weightage must total ${REQUIRED_TOTAL_WEIGHTAGE}% to submit (now ${total}%).`;
    }
  }

  return {
    rowErrors,
    formError,
    isValid: formError === "" && Object.keys(rowErrors).length === 0,
  };
};

/**
 * Manager feedback rows are looser than a self sheet: KPI is optional and
 * weightage is all-or-nothing rather than always required.
 */
export interface ValidateManagerRowsParams {
  rows: GoalRowDraft[];
}

export const validateManagerRows = ({
  rows,
}: ValidateManagerRowsParams): SelfFeedbackValidationResult => {
  // Reuse the row-level checks, without any of the submit-only requirements.
  const base = validateSelfFeedback({ rows, submit: false });

  const filled = rows.filter(isSendableRow);
  const withWeightage = filled.filter((row) => row.weightage.trim() !== "");

  let formError = base.formError;

  if (!formError && withWeightage.length > 0) {
    if (withWeightage.length !== filled.length) {
      formError = "Give every goal row a weightage, or none.";
    } else {
      const total = calculateTotalWeightage(rows);
      if (total !== REQUIRED_TOTAL_WEIGHTAGE) {
        formError = `Weightage must total ${REQUIRED_TOTAL_WEIGHTAGE}% (now ${total}%).`;
      }
    }
  }

  return {
    rowErrors: base.rowErrors,
    formError,
    isValid: formError === "" && Object.keys(base.rowErrors).length === 0,
  };
};
